import {
  getColumns,
  getSelectedWithoutHeader,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { DESKTOP_PROXY_AGENT } from "Constants";
import { NoResultsIndicator } from "elements";
import { usePolicyFilterValue } from "elements/Filter/PolicyFilter";
import {
  GroupPolicyFragment,
  HardwareModel,
  PolicyDeploymentScope,
  PolicyType,
  useHardwareModelsQuery,
  usePoliciesQuery
} from "generated/graphql";
import { infiniteNestLink, Link } from "polyrouter";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { needs, useGuard } from "rbac";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getDeviceModel } from "routes/Manage/Inventory/Settings/ProvisionedDeviceSettingsRouter";
import AddPolicyModal from "routes/Manage/Policy/AddPolicyModal";
import { DeletePolicyModal } from "routes/Manage/Policy/PolicyList";
import { State } from "store";
import { SortState } from "store/filters";
import { TABLES, TABLE_COLUMNS, TABLE_COLUMN_STATE } from "store/userMeta";
import { Guard, ProtectedRoute } from "TsRoleGuards";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { useDeviceGroup } from "../DeviceGroupsUtils";
import { Metric } from "./DeviceGroupDetailsRouter";
import { DeviceGroupTitle } from "./DeviceGroupOverview";

type GroupPolicyDetail = Pick<
  GroupPolicyFragment,
  "deviceModel" | "deviceModelUuid"
> & {
  policyIds: string[];
  deviceCount: number;
  settingsCount: number;
};

export const DeviceGroupPolicies: React.FC<{
  id: string;
  updateMetric: (metric: Metric) => void;
}> = ({ id, updateMetric }) => {
  const { t } = useTranslation();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const COLUMNS = TABLE_COLUMNS.DEVICE_GROUP_POLICIES;
  const { NAME: TABLE_NAME } = TABLES.DEVICE_GROUP_POLICIES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [searchTerm, setSearch] = useState<undefined | string>(undefined);
  const [sort, setSort] = useState({
    field: COLUMNS.DEVICE_MODEL,
    direction: "ASC"
  } as SortState);
  const [filterState, setFilter] = usePolicyFilterValue();
  useEffect(() => {
    if (!filterState.group) {
      setFilter({
        ...filterState,
        group: id
      });
    }
  }, [id, filterState, setFilter]);

  const columnTranslations = {
    [COLUMNS.DEVICE_MODEL]: t("DEVICE_MODEL"),
    [COLUMNS.DEVICE_COUNT]: t("DEVICE_COUNT"),
    [COLUMNS.SETTINGS_COUNT]: t("SETTINGS_COUNT")
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const { data: groupData, fetching: groupFetching } = useDeviceGroup(id);
  const name = groupData?.name ?? "";

  const { data: allHardwareModels } = useHardwareModelsQuery();
  let policySupportedModels: HardwareModel[] = [];
  if (allHardwareModels) {
    policySupportedModels = (allHardwareModels.hardwareModels as HardwareModel[])
      .filter(model => model.supportsProvisioning || model.supportsPolicies) // allow policies only for models that support provisioning or support policies
      .map(model => {
        const policyFormatModel = getDeviceModel(model.name);
        return {
          ...model,
          name: policyFormatModel
        };
      });
  }

  const { data: policiesData, fetching: policiesFetching } = usePoliciesQuery({
    variables: {
      groupId: id,
      scope: PolicyDeploymentScope.Group,
      tenantId: tenantId
    }
  });

  // we need to merge settings and software update polices so they apear as one row in the table
  const policies: Record<string, GroupPolicyDetail> = {};
  const groupDevicesByModel: Record<string, string[]> =
    groupData?.devices.reduce((devicesByModel, device) => {
      const model = device.hardwareModel ?? "";
      devicesByModel[model] = devicesByModel[model] ?? [];
      devicesByModel[model].push(device.id);

      const deviceModel = policySupportedModels.find(
        el => el.name === getDeviceModel(model)
      );
      if (
        deviceModel &&
        (device.provisioningEnabled ||
          device.proxyAgent === DESKTOP_PROXY_AGENT)
      ) {
        // if the model supports policies
        if (!policies[deviceModel.name]) {
          policies[deviceModel.name] = {
            policyIds: [],
            deviceModel: deviceModel.name,
            deviceModelUuid: deviceModel.id,
            deviceCount: 1,
            settingsCount: 0
          };
        } else {
          policies[deviceModel.name].deviceCount++;
        }
      }

      return devicesByModel;
    }, {} as Record<string, string[]>) ?? {};

  policiesData?.getPolicies?.forEach(policy => {
    if (policy && policy.policyId && policy.deviceModel) {
      const {
        policyId,
        deviceModel,
        deviceModelUuid,
        policyType,
        configurationAttributes
      } = policy;

      if (!policies[deviceModel]) {
        policies[deviceModel] = {
          policyIds: [policyId],
          deviceModel,
          deviceModelUuid,
          deviceCount: groupDevicesByModel[deviceModel]?.length ?? 0,
          settingsCount: 0
        };
      } else {
        policies[deviceModel].policyIds.push(policyId);
      }

      if (policyType === PolicyType.DeviceSettingsUpdate) {
        policies[deviceModel].settingsCount =
          configurationAttributes?.length ?? 0;
      }
    }
  });

  const models = Object.values(policies).map(policy => ({
    model: policy.deviceModel!,
    uuid: policy.deviceModelUuid
  }));

  const filteredPolicies = Object.values(policies).filter(
    policy =>
      !searchTerm ||
      policy.deviceModel?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  sortTableData(filteredPolicies, sort);

  useEffect(() => {
    updateMetric({
      type: t("POLICIES"),
      count: filteredPolicies.length
    });
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMetric, filteredPolicies.length]);

  const selectedModels = getSelectedWithoutHeader(selectedItems);
  const anySelected = selectedModels.length > 0;
  const itemChecked = itemCheckedWithSet(selectedItems);
  const toggleItem = toggleItemWithSet(
    filteredPolicies.length,
    selectedItems,
    setSelectedItems
  );

  const editRequirement = "UPDATE_SITES";
  const userCanEdit = useGuard(needs(editRequirement));
  // there might be one additional column besides those user choose to display (checkbox to select rows)
  const columnCount = shown.length + (userCanEdit ? 1 : 0);

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  const fetching = groupFetching || policiesFetching;
  const { Content } = Layout;
  return (
    <>
      <Content className="device-group-details">
        <DeviceGroupTitle groupName={name} page="POLICIES" />
        <div className="grouping pb-1 table-actions">
          <Guard requirement={editRequirement}>
            <Link
              to={infiniteNestLink("/group/add")}
              className="btn btn-primary"
            >
              {t("ADD")}
            </Link>
            <Link
              disabled={!anySelected}
              to={anySelected ? infiniteNestLink("/remove") : ""}
              className={`btn ${anySelected ? "" : "disabled"}`}
            >
              {t("DELETE")}
            </Link>
          </Guard>
          <TableSearchEdit
            search={{
              searchPlaceholder: `${t("SEARCH")} ${
                columnTranslations[COLUMNS.DEVICE_MODEL]
              }`,
              onSearch: setSearch
            }}
            edit={{
              tableName: TABLE_NAME,
              shownColumns: shown,
              hiddenColumns: hidden
            }}
          />
        </div>
        <div className="table-container">
          <table className="table custom-table table-filterable">
            <TableBulkItemHeader
              showChecks={userCanEdit}
              checked={itemChecked(HEADER_ID)}
              onCheck={() =>
                toggleAll(
                  filteredPolicies.map(policy => policy.deviceModel!),
                  selectedItems,
                  setSelectedItems
                )
              }
              replaceCheckWith={
                <input
                  type="checkbox"
                  data-testid="check-all"
                  onChange={() =>
                    toggleAll(
                      filteredPolicies
                        .filter(
                          model => model.deviceCount === 0 && model.deviceModel
                        )
                        .map(entry => entry.deviceModel!),
                      selectedItems,
                      setSelectedItems
                    )
                  }
                  checked={itemChecked(HEADER_ID)}
                  // If there are no user created policies, disable checkbox
                  disabled={
                    filteredPolicies.filter(model => model.deviceCount === 0)
                      .length === 0
                  }
                />
              }
            >
              {generateTableHeaders(shown, getTableHeaders)}
            </TableBulkItemHeader>
            <TableBody
              loading={fetching}
              cols={columnCount}
              accommodateScrollbar
            >
              {filteredPolicies.map(policy => (
                <PolicyRow
                  {...policy}
                  key={policy.deviceModel!}
                  groupId={id}
                  onCheck={() => toggleItem(policy.deviceModel!)}
                  checked={itemChecked(policy.deviceModel!)}
                  showChecks={userCanEdit}
                  shownColumns={shown.map(column => column.key)}
                />
              ))}
            </TableBody>
          </table>
          {!fetching && !filteredPolicies.length ? (
            <NoResultsIndicator title={t("NO_POLICIES")} />
          ) : null}
        </div>
      </Content>
      <ProtectedRoute
        nest
        path="/:scope/add"
        render={() => (
          <AddPolicyModal
            existingPolicies={models.map(bucket => {
              return { model: bucket.model, uuid: bucket.uuid };
            })}
          />
        )}
        requirement="update:device"
      />
      <ProtectedRoute nest path="/remove" requirement="update:device">
        <DeletePolicyModal
          selected={selectedModels.flatMap(model => policies[model].policyIds)}
          policies={filteredPolicies.flatMap(
            ({
              policyIds,
              deviceModel,
              deviceCount,
              deviceModelUuid,
              settingsCount
            }) =>
              policyIds.map(policyId => ({
                policyId: policyId,
                name: deviceModel!,
                count: deviceCount,
                uuid: deviceModelUuid,
                settings: settingsCount
              }))
          )}
          setSelected={setSelectedItems}
        />
      </ProtectedRoute>
    </>
  );
};

type PolicyRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
  groupId: string;
} & GroupPolicyDetail;

const PolicyRow: React.FC<PolicyRowProps> = ({
  deviceModel,
  deviceCount,
  settingsCount,
  checked,
  showChecks,
  onCheck,
  shownColumns,
  groupId
}) => {
  const { COLUMNS } = TABLES.DEVICE_GROUP_POLICIES;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.DEVICE_MODEL:
        content = (
          <Link
            persist={["group", "previous"]}
            to={{
              pathname: "/manage/policy/details/settings",
              search: `deviceModel=${deviceModel}&group=${groupId}&previous=group`
            }}
          >
            {handleNullFields(deviceModel)}
          </Link>
        );
        break;
      case COLUMNS.DEVICE_COUNT:
        content = deviceCount;
        break;
      case COLUMNS.SETTINGS_COUNT:
        content = settingsCount;
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return deviceModel ? (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(deviceModel)}
      showChecks={showChecks}
      replaceCheckWith={
        <input
          type="checkbox"
          data-testid="check-item"
          onChange={() => onCheck(deviceModel)}
          checked={checked}
          disabled={deviceCount > 0}
        />
      }
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  ) : null;
};
